import React, { useState, useEffect } from 'react';
import { Input, Button, message, Space, Typography, Alert, Modal } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

const { Title, Text } = Typography;

// 需要显示为"已涨粉"的状态
const GROWTH_STATUSES = ['需结账', '打款中', '已付款', '已涨粉'];

function getDisplayStatus(statusName) {
  if (GROWTH_STATUSES.includes(statusName)) return '已涨粉';
  return statusName || '未设置';
}

function isGrowthStatus(statusName) {
  return GROWTH_STATUSES.includes(statusName);
}

function QueryOrder() {
  const [searchParams] = useSearchParams();
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [results, setResults] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id && id.trim()) {
      setAccountName(id.trim());
      doSearch(id.trim());
    }
  }, []);

  const doSearch = async (name) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/orders/query', { params: { account_name: name } });
      if (res.data.code === 200) {
        setResults(res.data.data);
        if (res.data.data.length === 0) {
          message.info('未找到相关订单');
        }
      }
    } catch (err) {
      message.error('查询失败，请稍后重试');
    }
    setLoading(false);
  };

  const handleSearch = () => {
    const name = accountName.trim();
    if (!name) {
      message.warning('请输入抖音号');
      return;
    }
    doSearch(name);
  };

  const handleConfirmAuth = async () => {
    const name = accountName.trim();
    if (!name) return;
    setConfirming(true);
    try {
      const res = await axios.post('/api/orders/query/confirm-auth', { account_name: name });
      if (res.data.code === 200) {
        message.success(res.data.message);
        handleSearch();
      } else {
        message.error(res.data.message || '操作失败');
      }
    } catch (err) {
      message.error('操作失败，请稍后重试');
    }
    setConfirming(false);
  };

  // 检查结果中是否有"已发送授权"的状态
  const hasAuthStatus = results?.some(r => r.status_name === '已发送授权');
  // 检查是否有已涨粉的订单
  const hasGrowthStatus = results?.some(r => isGrowthStatus(r.status_name));

  const getStatusStyle = (statusName) => {
    if (statusName === '已发送授权') return { color: '#faad14', fontWeight: 'bold' };
    if (isGrowthStatus(statusName)) return { color: '#52c41a', fontWeight: 'bold' };
    return {};
  };

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #e8f4fd 0%, #f0f2f5 30%)', padding: '16px 12px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 28, paddingTop: 12 }}>
          <Title level={3} style={{ marginBottom: 8, fontSize: 'clamp(20px, 5vw, 26px)', color: '#1a1a1a', fontWeight: 700 }}>涨粉状态查询</Title>
          <Text type="secondary" style={{ fontSize: 'clamp(13px, 3vw, 14px)' }}>输入您的抖音号，查询涨粉进度</Text>
        </div>

        {results === null && (
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 20,
            flexWrap: 'wrap'
          }}>
            <div style={{ textAlign: 'center', animation: 'fadeInUp 0.5s ease-out' }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: '#1677ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, margin: '0 auto 8px' }}>1</div>
              <Text style={{ fontSize: 13, color: '#666' }}>输入抖音号</Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', color: '#ccc', fontSize: 20, marginTop: -12 }}>→</div>
            <div style={{ textAlign: 'center', animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
              <div style={{ width: 40, height: 40, borderRadius: 20, background: '#1677ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, margin: '0 auto 8px' }}>2</div>
              <Text style={{ fontSize: 13, color: '#666' }}>点击查询</Text>
            </div>
          </div>
        )}

        <div style={{
          background: '#fff',
          borderRadius: 8,
          padding: '16px 12px',
          marginBottom: 16,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="large"
              placeholder="请输入抖音号"
              value={accountName}
              onChange={e => setAccountName(e.target.value)}
              onPressEnter={handleSearch}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              size="large"
              icon={<SearchOutlined />}
              loading={loading}
              onClick={handleSearch}
            >
              查询
            </Button>
          </Space.Compact>
        </div>

        {results !== null && (
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: '16px 12px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            {results.length > 0 ? (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <Text>共找到 <Text strong>{results.length}</Text> 条订单</Text>
                  {hasAuthStatus && (
                    <Button
                      type="primary"
                      loading={confirming}
                      onClick={handleConfirmAuth}
                    >
                      全部确认授权
                    </Button>
                  )}
                </div>
                {hasGrowthStatus && (
                  <Alert
                    message="有效粉和电商带货权限48小时之后更新状态哈，有些号需要等待2-5天，耐心等待即可抖音的规则哈，更新出来后及时开通带货功能"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {results.map((record, index) => {
                  const hasAuth = record.status_name === '已发送授权';
                  const display = getDisplayStatus(record.status_name);
                  return (
                    <div key={index} style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: '18px 16px',
                      marginBottom: 14,
                      border: '1px solid #eee',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      animation: `fadeInUp 0.4s ease-out ${index * 0.08}s both`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <Text strong style={{ fontSize: 17, color: '#1a1a1a' }}>{record.account_name}</Text>
                        <Text style={{ ...getStatusStyle(record.status_name), fontSize: 14, background: hasAuth ? '#fff7e6' : isGrowthStatus(record.status_name) ? '#f6ffed' : '#f5f5f5', padding: '4px 12px', borderRadius: 20 }}>{display}</Text>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13, color: '#555' }}>
                        {record.group_name && <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>群名</span> {record.group_name}</div>}
                        {record.period && <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>期数</span> {record.period}</div>}
                        {record.contact_person && <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>跟进老师</span> {record.contact_person}</div>}
                        {record.initial_fans != null && <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>初始粉丝</span> {record.initial_fans}</div>}
                        <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>下单</span> {record.order_time ? dayjs(record.order_time).format('MM-DD HH:mm') : '-'}</div>
                        <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>更新</span> {record.updated_at ? dayjs(record.updated_at).format('MM-DD HH:mm') : '-'}</div>
                        {record.complete_date && <div style={{ padding: '2px 0' }}><span style={{ color: '#999' }}>完成</span> {dayjs(record.complete_date).format('MM-DD')}</div>}
                      </div>
                      {/* 状态引导教程 */}
                      {record.status_name === '等待发授权' || record.status_name === '未开通抖加/搜到不到' ? (
                        <div style={{ marginTop: 14, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                          <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                            message="按教程开通好抖加及打开搜索抖音号权限，等待老师安排发送授权进行涨粉"
                          />
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <Text strong style={{ display:'block', marginBottom: 6, fontSize: 13 }}>📖 开抖加教程</Text>
                              <img src="/kaidoujia.jpg" alt="开抖加教程" onClick={() => setPreviewImg('/kaidoujia.jpg')} style={{ width:'100%', borderRadius: 8, border:'1px solid #eee', cursor:'pointer' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <Text strong style={{ display:'block', marginBottom: 6, fontSize: 13 }}>📖 开搜索教程</Text>
                              <img src="/kaisousuo.jpg" alt="开搜索教程" onClick={() => setPreviewImg('/kaisousuo.jpg')} style={{ width:'100%', borderRadius: 8, border:'1px solid #eee', cursor:'pointer' }} />
                            </div>
                          </div>
                        </div>
                      ) : record.status_name === '已发送授权' ? (
                        <div style={{ marginTop: 14, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                          <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                            message="按教程提示打开抖音去确认授权，确认后回来点「我已同意授权」，就可以正常安排涨粉了"
                          />
                          <div>
                            <Text strong style={{ display:'block', marginBottom: 6, fontSize: 13 }}>📖 确认授权教程</Text>
                            <img src="/shouquan.jpg" alt="确认授权教程" onClick={() => setPreviewImg('/shouquan.jpg')} style={{ width:'100%', maxWidth: 500, borderRadius: 8, border:'1px solid #eee', cursor:'pointer' }} />
                          </div>
                          <div style={{ marginTop: 12, textAlign: 'right' }}>
                            <Button type="primary" shape="round" loading={confirming} onClick={handleConfirmAuth}>我已同意授权</Button>
                          </div>
                        </div>
                      ) : record.status_name === '已确认授权' ? (
                        <div style={{ marginTop: 14, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                          <Alert type="success" showIcon
                            message="授权确认后，耐心等待老师安排涨粉，安排后会状态会改为「涨粉中」"
                          />
                        </div>
                      ) : hasAuth ? (
                        <div style={{ marginTop: 14, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                          <Button type="primary" shape="round" loading={confirming} onClick={handleConfirmAuth}>我已同意授权</Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                未找到相关订单
              </div>
            )}
          </div>
        )}
      </div>
    </div>

      <Modal open={!!previewImg} footer={null} onCancel={() => setPreviewImg(null)} width="90vw" style={{ top: 20 }}>
        {previewImg && <img src={previewImg} alt="教程预览" style={{ width: '100%', borderRadius: 8 }} />}
      </Modal>
    </>
  );
}

export default QueryOrder;
